"use client";

import Link from "next/link";
import Script from "next/script";
import { displayFont } from "@/components/content-blocks/band-helpers";
import { useRouter } from "next/router";
import { useState, useTransition } from "react";
import {
  buildDeliveryQuoteRedirectUrl,
  isValidUkPostcode,
  resolveBoroughLinkForAddress,
  type DeliveryQuoteAddress,
  type DeliveryQuoteLookupResponse,
} from "@/lib/delivery-quote";
import type { PageLinkSummary } from "@/lib/storefront-types";
import { pushDataLayerEvent } from "@/lib/analytics";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready(callback: () => void): void;
        execute(siteKey: string, options: { action: string }): Promise<string>;
      };
    };
  }
}

function requestCaptchaToken(siteKey: string) {
  return new Promise<string>((resolve, reject) => {
    const enterprise = window.grecaptcha?.enterprise;
    if (!enterprise) {
      reject(
        new Error("The verification service is still loading. Please try again."),
      );
      return;
    }

    enterprise.ready(async () => {
      try {
        const token = await enterprise.execute(siteKey, {
          action: "postcode_lookup",
        });

        if (!token) {
          reject(
            new Error("Verification could not be completed. Please try again."),
          );
          return;
        }

        resolve(token);
      } catch {
        reject(
          new Error("Verification could not be completed. Please try again."),
        );
      }
    });
  });
}

type DeliveryQuotePageProps = {
  boroughLinks: PageLinkSummary[];
  recaptchaSiteKey?: string | null;
  title: string;
};

export function DeliveryQuotePage({
  boroughLinks,
  recaptchaSiteKey,
  title,
}: DeliveryQuotePageProps) {
  return (
    <FoodDeliveryQuotePage
      boroughLinks={boroughLinks}
      recaptchaSiteKey={recaptchaSiteKey}
      title={title}
    />
  );
}

type FoodDeliveryQuotePageProps = {
  boroughLinks: PageLinkSummary[];
  recaptchaSiteKey?: string | null;
  title: string;
};

function FoodDeliveryQuotePage({
  boroughLinks,
  recaptchaSiteKey,
  title,
}: FoodDeliveryQuotePageProps) {
  const isCaptchaConfigured = Boolean(recaptchaSiteKey);
  const router = useRouter();
  const [postcode, setPostcode] = useState("");
  const [resolvedPostcode, setResolvedPostcode] = useState<string | null>(null);
  const [results, setResults] = useState<DeliveryQuoteAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [outsideLondonAddress, setOutsideLondonAddress] =
    useState<DeliveryQuoteAddress | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isRedirectPending, startRedirectTransition] = useTransition();
  const isBusy = isLookingUp || isRedirectPending;
  const selectedAddress =
    results.find((address) => address.id === selectedAddressId) ?? null;

  async function handleLookupSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!isValidUkPostcode(postcode)) {
      setErrorMessage("Enter a valid UK postcode to find your address.");
      setResolvedPostcode(null);
      setResults([]);
      setSelectedAddressId("");
      setOutsideLondonAddress(null);
      pushDataLayerEvent("postcode_check", { postcode_valid: false });
      return;
    }

    pushDataLayerEvent("postcode_check", { postcode_valid: true });
    setErrorMessage(null);
    setOutsideLondonAddress(null);
    setIsLookingUp(true);

    try {
      let captchaToken: string | undefined;
      if (isCaptchaConfigured && recaptchaSiteKey) {
        try {
          captchaToken = await requestCaptchaToken(recaptchaSiteKey);
        } catch (captchaError) {
          setErrorMessage(
            captchaError instanceof Error
              ? captchaError.message
              : "Verification failed. Please try again.",
          );
          setIsLookingUp(false);
          return;
        }
      }

      const lookupParams = new URLSearchParams({
        postcode: postcode.trim(),
      });
      if (captchaToken) {
        lookupParams.set("captchaToken", captchaToken);
      }

      const response = await fetch(
        `/api/postcode-lookup?${lookupParams.toString()}`,
        {
          headers: {
            Accept: "application/json",
          },
          cache: "no-store",
        },
      );

      const payload = (await response
        .json()
        .catch(() => null)) as
        | (DeliveryQuoteLookupResponse & {
            error?: {
              code?: string;
              message?: string;
            };
          })
        | null;

      if (!response.ok) {
        setResolvedPostcode(null);
        setResults([]);
        setSelectedAddressId("");
        setErrorMessage(
          payload?.error?.message ??
            "We could not look up that postcode right now.",
        );
        return;
      }

      const matchedResults = payload?.results ?? [];
      setResolvedPostcode(payload?.postcode ?? postcode.trim());
      setResults(matchedResults);
      setSelectedAddressId("");

      if (matchedResults.length === 0) {
        setErrorMessage("No addresses were found for that postcode.");
        return;
      }
    } catch {
      setResolvedPostcode(null);
      setResults([]);
      setSelectedAddressId("");
      setErrorMessage(
        "We could not reach the postcode lookup service. Please try again.",
      );
    } finally {
      setIsLookingUp(false);
    }
  }

  function handleAddressContinue(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!selectedAddress) {
      setErrorMessage("Choose your address to continue.");
      setOutsideLondonAddress(null);
      return;
    }

    const matchingBoroughLink = resolveBoroughLinkForAddress(
      selectedAddress,
      boroughLinks,
    );

    if (!matchingBoroughLink) {
      setErrorMessage(null);
      setOutsideLondonAddress(selectedAddress);
      return;
    }

    setErrorMessage(null);
    setOutsideLondonAddress(null);

    startRedirectTransition(() => {
      void router.push(
        buildDeliveryQuoteRedirectUrl(matchingBoroughLink.path, selectedAddress),
      );
    });
  }

  return (
    <section className="text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
        <div className="storefront-page-panel-soft overflow-hidden rounded-[2rem]">
          <div className="px-6 py-7 sm:px-8 sm:py-8 lg:px-10">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.26em] text-white/70">
              Delivery Quote
            </p>
            <h1 className={`${displayFont.className} mt-3 text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight text-white`}>
              {title}
            </h1>
            <p className="mt-2 max-w-2xl text-[0.98rem] leading-7 text-white/80 sm:text-[1.02rem]">
              We deliver delicious Nigerian Meals to any address in London
            </p>

            {results.length === 0 ? (
              <form onSubmit={handleLookupSubmit} className="mt-5 space-y-4">
                <label className="grid gap-2.5">
                  <span className="px-1 text-[0.76rem] font-semibold uppercase tracking-[0.2em] text-white/70">
                    Postcode
                  </span>
                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                    <input
                      type="text"
                      name="postcode"
                      value={postcode}
                      onChange={(event) => {
                        setPostcode(event.target.value);
                        setResolvedPostcode(null);
                        setResults([]);
                        setSelectedAddressId("");
                        setErrorMessage(null);
                        setOutsideLondonAddress(null);
                      }}
                      autoComplete="postal-code"
                      inputMode="text"
                      className="storefront-page-input min-h-14 w-full rounded-[1.25rem] px-5 py-4 text-base"
                      placeholder="Enter your London postcode"
                      aria-describedby="delivery-quote-status"
                    />
                    <button
                      type="submit"
                      disabled={isBusy}
                      className="storefront-page-button inline-flex min-h-14 items-center justify-center rounded-full px-6 text-base font-semibold whitespace-nowrap"
                    >
                      {isLookingUp ? "Finding..." : "Find address"}
                    </button>
                  </div>
                </label>

                {errorMessage ? (
                  <p
                    id="delivery-quote-status"
                    className="min-h-6 text-sm leading-6 text-red-300"
                    role="status"
                  >
                    {errorMessage}
                  </p>
                ) : null}
              </form>
            ) : (
              <form onSubmit={handleAddressContinue} className="mt-5">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-white/60">
                    {results.length} address{results.length === 1 ? "" : "es"} for <span className="font-semibold text-white/80">{resolvedPostcode}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setResults([]);
                      setSelectedAddressId("");
                      setResolvedPostcode(null);
                      setErrorMessage(null);
                      setOutsideLondonAddress(null);
                    }}
                    className="text-sm font-medium text-white/70 underline underline-offset-2 transition-colors hover:text-white"
                  >
                    Change postcode
                  </button>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <div>
                    <label htmlFor="delivery-quote-address-select" className="sr-only">
                      Select your address
                    </label>
                    <select
                      id="delivery-quote-address-select"
                      name="delivery-quote-address"
                      value={selectedAddressId}
                      onChange={(event) => {
                        setSelectedAddressId(event.target.value);
                        setErrorMessage(null);
                        setOutsideLondonAddress(null);
                      }}
                      className="storefront-page-input min-h-14 w-full rounded-[1.25rem] px-5 py-4 text-base"
                    >
                      <option value="">Select your address...</option>
                      {results.map((address) => (
                        <option key={address.id} value={address.id}>
                          {[address.address1, address.address2, address.province, address.postalCode].filter(Boolean).join(", ")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={isBusy}
                    className="storefront-page-button inline-flex min-h-14 items-center justify-center rounded-full px-6 text-base font-semibold whitespace-nowrap"
                  >
                    {isRedirectPending
                      ? "Opening..."
                      : "Select"}
                  </button>
                </div>

                {selectedAddress ? (
                  <div className="mt-3 rounded-[1.25rem] border border-white/20 bg-white/10 px-5 py-3.5">
                    <p className="text-base leading-6 text-white">
                      <span className="font-semibold">{selectedAddress.address1}</span>
                      {selectedAddress.address2 ? <>, {selectedAddress.address2}</> : null}
                      {selectedAddress.province ? <>, {selectedAddress.province}</> : null}
                      {selectedAddress.postalCode ? <>, <span className="font-semibold">{selectedAddress.postalCode}</span></> : null}
                    </p>
                  </div>
                ) : null}

                {errorMessage ? (
                  <p className="mt-2 text-sm leading-6 text-red-300" role="status">
                    {errorMessage}
                  </p>
                ) : null}
              </form>
            )}

            {outsideLondonAddress ? (
              <div className="storefront-page-panel mt-6 rounded-[1.6rem] px-5 py-5 sm:px-6">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.26em] text-white/70">
                  Outside London
                </p>
                <h2 className="mt-2 text-[1.45rem] font-semibold tracking-tight text-white">
                  We can still help with that address.
                </h2>
                <p className="mt-3 text-sm leading-6 text-white/80 sm:text-[0.98rem]">
                  Looks like{" "}
                  <span className="font-semibold">{outsideLondonAddress.address1}</span>{" "}
                  sits outside of our London delivery boroughs. Don&apos;t worry, we can
                  still deliver to you through our sister site.
                </p>
                <div className="mt-5">
                  <Link
                    href="https://www.asorockfrozen.com"
                    target="_blank"
                    rel="noreferrer"
                    className="storefront-page-button-dark inline-flex min-h-12 items-center justify-center rounded-full px-6 text-sm font-semibold"
                  >
                    Visit AsoRockFrozen.com
                  </Link>
                </div>
              </div>
            ) : null}
          </div>
        </div>

      </div>
      {isCaptchaConfigured ? (
        <Script
          src={`https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(
            recaptchaSiteKey!,
          )}`}
          strategy="afterInteractive"
        />
      ) : null}
    </section>
  );
}
